import { type FC, useState } from "react"

import { type CommunityAccess } from "@forento/core/models/community"

import Button, { SubmitButton } from "~/components/Button"
import InputField from "~/components/InputField"
import { DefaultModal, ModalButtons, ModalInputs, ModalText, ModalTitle } from "~/components/Modal"
import PartialLoadingPage from "~/components/PartialLoadingPage"
import { useAlert } from "~/contexts/AlertContext"
import { usePlatform } from "~/contexts/UserContext"
import routes from "~/utilities/routes"
import trpc, { query } from "~/utilities/trpc"

import ChannelAccessInputs, {
	type MembershipAccess,
	type ProductAccess,
	useChannelProducts,
} from "./ChannelAccessInputs"

type Props = { isOpen: boolean; onClose(): void; onCreated(): Promise<unknown> }
const CreateModal: FC<Props> = ({ isOpen, onClose, onCreated }) => {
	const platform = usePlatform()!
	const alert = useAlert()

	const [name, setName] = useState("")
	const [defaultAccess, setDefaultAccess] = useState<CommunityAccess | null>("post")
	const [membershipAccess, setMembershipAccess] = useState<MembershipAccess[]>([])
	const [productAccess, setProductAccess] = useState<ProductAccess[]>([])
	const [isSubmitting, setSubmitting] = useState(false)

	const memberships = query.membership.list.useQuery()
	const products = useChannelProducts()

	async function handleCreate() {
		setSubmitting(true)
		try {
			await trpc.community.createChannel.mutate({ name, defaultAccess, membershipAccess, productAccess })
			await onCreated()
			setName("")
			setDefaultAccess("post")
			setMembershipAccess([])
			setProductAccess([])
			onClose()
		} catch (error) {
			setSubmitting(false)
			console.error(error)
			await alert.show("Error", "Failed to create the channel. Please try again.")
		}
		setSubmitting(false)
	}

	if (!platform.plan.access.communityChannels) {
		const exampleChannels = [
			{ emoji: "💬", name: "Community Chat" },
			{ emoji: "🎓", name: "Course Updates" },
			{ emoji: "🚀", name: "Member Wins" },
			{ emoji: "📢", name: "Announcements" },
		]

		return (
			<DefaultModal onClose={onClose} isOpen={isOpen}>
				<ModalTitle>Unlock community channels</ModalTitle>
				<ModalText>
					Channels help you organize your community into focused spaces. Upgrade your plan to start creating
					them.
				</ModalText>
				<div className="mx-8 mb-5 overflow-hidden rounded-xl border border-gray-200 bg-gray-50">
					<div className="border-b border-gray-200 bg-white px-4 py-2.5">
						<p className="text-xs font-semibold tracking-wider text-gray-400 uppercase">Channels</p>
					</div>
					<ul className="p-2">
						{exampleChannels.map(channel => (
							<li
								key={channel.name}
								className="first:bg-primary/10 first:text-primary flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm text-gray-500 first:font-medium"
							>
								<span>{channel.emoji}</span>
								<span>{channel.name}</span>
							</li>
						))}
					</ul>
				</div>
				<ModalButtons>
					<Button variant="secondary" onClick={onClose}>
						Cancel
					</Button>
					<Button variant="primary" onClick={routes.settings.billing.plans()}>
						Upgrade plan
					</Button>
				</ModalButtons>
			</DefaultModal>
		)
	}

	return (
		<DefaultModal onClose={onClose} isOpen={isOpen} width={680} onSubmit={handleCreate}>
			<ModalTitle>Create a new channel</ModalTitle>
			{memberships.error || products.error ? (
				<ModalText>Failed to load data.</ModalText>
			) : memberships.data === undefined || products.data === undefined ? (
				<PartialLoadingPage />
			) : (
				<>
					<ModalText>Channels organize your community's posts into separate spaces.</ModalText>
					<ModalInputs>
						<InputField label="Channel name" value={name} onChange={setName} />
						<ChannelAccessInputs
							memberships={memberships.data}
							products={products.data}
							defaultAccess={defaultAccess}
							membershipAccess={membershipAccess}
							productAccess={productAccess}
							onDefaultAccessChange={setDefaultAccess}
							onMembershipAccessChange={setMembershipAccess}
							onProductAccessChange={setProductAccess}
						/>
					</ModalInputs>
					<ModalButtons>
						<Button variant="secondary" onClick={onClose} isDisabled={isSubmitting}>
							Cancel
						</Button>
						<SubmitButton variant="primary" isLoading={isSubmitting}>
							Create
						</SubmitButton>
					</ModalButtons>
				</>
			)}
		</DefaultModal>
	)
}

export default CreateModal
